import { AnyAction } from "redux";

import {
  INIT_AGENT_REPLACEMENT_ENDPOINT,
  JOBS2_ENDPOINT,
  JOBS_CANCELLATION_PAYMENT_ENDPOINT,
  JOBS_CANCELLATION_REASON_ENDPOINT,
  JOBS_CANCELLATION_START_ENDPOINT,
  JOBS_KEY_PICK_ENDPOINT,
  JOBS_RATING_ENDPOINT,
  JOBS_SPECIAL_INSTRUCTIONS_ENDPOINT,
  JOB_BY_UUID_ENDPOINT,
  INIT_JOB_RESCHEDULING,
  JOB_RESCHEDULING_AVAILABILITIES,
  JOB_RESCHEDULING_CHANGE_REQUEST,
  AUTOMATIC_AGENT_REPLACEMENT,
} from "@config/endpoints";

import { SubmittedCancellationPaymentMethodDto } from "@typings/jobCancellation";
import {
  ICancelHandleReasonResponse,
  ICancelSubmitReason,
  JobRating,
  JOB_FETCH_TYPE,
  KeyPick,
  SpecialInstructions,
  CancellationReasonItem,
  CHANGE_REQUEST_APPLY_CHANGES_TYPE,
  JOBS_REFERER,
} from "@typings/jobs";
import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { ASYNC_ACTION_TYPES } from "@services/FetchFacade";
import { buildUrl } from "@services/BuildUrl";
import { nullifyString } from "@services/NullifyString";

import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { genericPendingAction } from "@app/services/GenericPendingAction";

import { IStore } from "../reducers";

import {
  getJob,
  getJobs2Location,
  getJobs2ByLocationBeyondPlanningWindow,
} from "./jobs2.selectors";

const action = new ReduxActionNameCreator("jobs2");

export const FETCH_JOBS2 = action.make("fetch jobs2");
export const FETCH_NEXT_UPCOMING_JOB = action.make("fetch next upcoming job");
export const FETCH_JOB_BY_UUID = action.make("fetch job by uuid");
export const CANCEL_START = action.make("cancellation start");
export const CANCEL_REASON = action.make("cancellation pass reason");
export const CANCEL_PAYMENT = action.make("cancellation payment process");
export const CANCEL_DONE = action.make("cancellation done");
export const INIT_AGENT_REPLACEMENT = action.make("init agent replacement");
export const RATE_JOB = action.make("rate job");
export const SET_JOB_RATING = action.make("set job rating");
export const SET_KEY_PICK = action.make("set keyPick");
export const SET_SPECIAL_INSTRUCTIONS = action.make("set special instructions");
export const SET_CANCELLATION_REASON = action.make(
  "select cancellation reason",
);
export const INITIALIZE_JOB_RESCHEDULING = action.make(
  "initialize job rescheduling",
);
export const FETCH_RESCHEDULING_AVAILABILITIES = action.make(
  "fetch rescheduling availabilities",
);
export const SUBMIT_RESCHEDULING_CHANGE_REQUEST = action.make(
  "submit rescheduling change request",
);
export const INITIALIZE_AUTOMATIC_AGENT_REPLACEMENT = action.make(
  "initialize automatic agent replacement",
);
export const FETCH_JOBS_BEYOND_PLANNING_WINDOW = action.make(
  "fetch jobs beyond planning window",
);

interface FetchJobs2Params {
  jobState?: JOB_FETCH_TYPE;
  locationUuid?: string;
  maxResults?: number;
  withoutCursor?: boolean;
  excludeCancelled?: boolean;
}

export const fetchJobs2 =
  ({
    locationUuid = "",
    jobState = JOB_FETCH_TYPE.UPCOMING,
    maxResults = 5,
    withoutCursor,
    excludeCancelled,
  }: FetchJobs2Params) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const location = getJobs2Location(store(), locationUuid);
    const hasNextPage =
      (jobState === JOB_FETCH_TYPE.UPCOMING &&
        location?.get("hasNextPageUpcoming")) ||
      (jobState === JOB_FETCH_TYPE.PREVIOUS &&
        location?.get("hasNextPagePrevious"));

    const cursorFromLocation =
      hasNextPage && !withoutCursor ? location?.get("cursor") : undefined;

    const url = buildUrl(JOBS2_ENDPOINT)
      .addQuery("type", jobState)
      .addQuery("maxResults", maxResults)
      .addQuery("startingAfter", cursorFromLocation, { optional: true })
      .addQuery("locationUuid", locationUuid, { optional: true })
      .addQuery(
        "excludeCancelled",
        excludeCancelled ? String(excludeCancelled) : "",
        {
          optional: true,
        },
      )
      .done();

    const action = await asyncActionCreator({
      extra: {
        uiState: jobState,
      },
      retryConnection: false,
      url,
      action: FETCH_JOBS2,
    });

    return dispatch(action);
  };

export const fetchJobByUuid =
  (uuid: string, jobFetchType?: JOB_FETCH_TYPE) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: JOB_BY_UUID_ENDPOINT.replace("{uuid}", uuid),
      action: FETCH_JOB_BY_UUID,
      params: {
        jobFetchType,
      },
    });

    return dispatch(action);
  };

export const fetchNextUpcomingJob =
  () =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = buildUrl(JOBS2_ENDPOINT)
      .addQuery("type", JOB_FETCH_TYPE.UPCOMING)
      .addQuery("maxResults", 1)
      .addQuery("excludeCancelled", "true")
      .done();

    const action = await asyncActionCreator({
      url: url,
      action: FETCH_NEXT_UPCOMING_JOB,
      retryConnection: false,
    });

    return dispatch(action);
  };

export const fetchJobsBeyondPlanningWindow =
  ({
    locationUuid = "",
    maxResults = 5,
    withoutCursor,
  }: Pick<FetchJobs2Params, "locationUuid" | "maxResults" | "withoutCursor">) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_JOBS_BEYOND_PLANNING_WINDOW));

    const location = getJobs2ByLocationBeyondPlanningWindow(
      store(),
      locationUuid,
    );
    const hasNextPage = location?.get("hasNextPage");
    const cursorFromLocation =
      hasNextPage && !withoutCursor ? location?.get("cursor") : undefined;

    const url = buildUrl(JOBS2_ENDPOINT)
      .addQuery("type", JOB_FETCH_TYPE.BEYOND_PLANNING_WINDOW)
      .addQuery("maxResults", maxResults)
      .addQuery("startingAfter", cursorFromLocation, { optional: true })
      .addQuery("locationUuid", locationUuid, { optional: true })
      .addQuery("excludeCancelled", "true")
      .done();

    const action = await asyncActionCreator({
      retryConnection: false,
      url,
      action: FETCH_JOBS_BEYOND_PLANNING_WINDOW,
    });

    return dispatch(action);
  };

/**
 * Cancellation flow
 */

export const cancelStart =
  (id: string, referer?: JOBS_REFERER) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: buildUrl(JOBS_CANCELLATION_START_ENDPOINT)
        .setQuery("jobId", id)
        .addQuery("referer", referer ?? JOBS_REFERER.DEFAULT)
        .done(),
      action: CANCEL_START,
      method: ASYNC_ACTION_TYPES.POST,
      params: {
        id,
      },
    });

    return dispatch(action);
  };

export const cancelSubmitReason =
  ({ jobId, cancellationId, body }: ICancelSubmitReason) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: JOBS_CANCELLATION_REASON_ENDPOINT.replace(
        "{cancellationRequestUuid}",
        cancellationId,
      ),
      action: CANCEL_REASON,
      method: ASYNC_ACTION_TYPES.POST,
      body,
      params: {
        id: jobId,
      },
    });

    return dispatch(action);
  };

export const cancelHandlePayment =
  ({
    jobId,
    cancellationId,
    body,
    cancellationV2Enabled,
  }: ICancelHandleReasonResponse) =>
  async (dispatch: ThunkActionDispatch) => {
    const action =
      await asyncActionCreator<SubmittedCancellationPaymentMethodDto>({
        url: JOBS_CANCELLATION_PAYMENT_ENDPOINT.replace(
          "{cancellationRequestUuid}",
          cancellationId,
        ),
        action: CANCEL_PAYMENT,
        method: ASYNC_ACTION_TYPES.POST,
        params: {
          id: jobId,
          cancellationId,
          cancellationV2Enabled,
        },
        body,
      });

    return dispatch(action);
  };

export const cancelFinished = (
  jobId: string,
  amount: number,
  jobLocationUuid: string,
): AnyAction => ({
  type: CANCEL_DONE,
  payload: {
    id: jobId,
    amount,
    jobLocationUuid,
  },
});

export const initAgentReplacement =
  (jobId: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: INIT_AGENT_REPLACEMENT_ENDPOINT.replace("{uuid}", jobId),
      action: INIT_AGENT_REPLACEMENT,
      method: ASYNC_ACTION_TYPES.POST,
      params: {
        id: jobId,
      },
    });

    return dispatch(action);
  };

export const setCancellationReason = (
  reason: CancellationReasonItem,
): AnyAction => ({
  type: SET_CANCELLATION_REASON,
  payload: {
    reason,
  },
});

/**
 * Rating
 */

// rating v1 submit action
export const rateJob =
  (id: string, body: JobRating, locationUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = buildUrl(JOBS_RATING_ENDPOINT).setPath("uuid", id).done();

    if (typeof body.rating === "undefined") {
      body.rating = null;
    }

    const action = await asyncActionCreator({
      url,
      body,
      params: {
        id,
        ...body,
        locationUuid,
      },
      action: RATE_JOB,
      method: ASYNC_ACTION_TYPES.POST,
    });

    return dispatch(action);
  };

// updating state after submitting rating v2
export const setJobRating = (jobUuid: string, rating: number): AnyAction => ({
  type: SET_JOB_RATING,
  payload: {
    jobUuid,
    rating,
  },
});

/**
 * Key pick
 */

export const setKeyPick =
  (id: string, payload: KeyPick) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const url = JOBS_KEY_PICK_ENDPOINT.replace("{uuid}", id);

    const locationUuid = getJob(store(), id).get("locationUuid");

    const body = {
      ...payload,
      locationComment: nullifyString(payload.locationComment),
    };

    const action = await asyncActionCreator({
      url,
      body,
      params: {
        ...body,
        id,
        locationUuid,
      },
      action: SET_KEY_PICK,
      method: ASYNC_ACTION_TYPES.POST,
    });

    return dispatch(action);
  };

export const setSpecialInstructions =
  (id: string, payload: SpecialInstructions) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const url = JOBS_SPECIAL_INSTRUCTIONS_ENDPOINT.replace("{uuid}", id);

    const locationUuid = getJob(store(), id).get("locationUuid");

    const body = {
      ...payload,
      specialInstructions: nullifyString(payload.specialInstructions),
    };

    const action = await asyncActionCreator({
      url,
      body,
      params: {
        ...body,
        id,
        locationUuid,
      },
      action: SET_SPECIAL_INSTRUCTIONS,
      method: ASYNC_ACTION_TYPES.POST,
    });

    return dispatch(action);
  };

export const initJobRescheduling =
  (jobUuid: string) =>
  async (
    dispatch: ThunkActionDispatch,
    store: () => IStore,
  ): Promise<AsyncActionResult> => {
    const url = buildUrl(INIT_JOB_RESCHEDULING).setPath("uuid", jobUuid).done();

    const action = await asyncActionCreator({
      url,
      action: INITIALIZE_JOB_RESCHEDULING,
    });

    return dispatch(action);
  };

export const fetchReschedulingAvailabilities =
  (jobUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = buildUrl(JOB_RESCHEDULING_AVAILABILITIES)
      .setPath("uuid", jobUuid)
      .done();

    const action = await asyncActionCreator({
      url,
      action: FETCH_RESCHEDULING_AVAILABILITIES,
    });

    return dispatch(action);
  };

interface SubmitChangeRequestChangesParams {
  agentId: string;
  executionDate: string;
  duration: number;
  applyChanges: CHANGE_REQUEST_APPLY_CHANGES_TYPE;
}

export const submitRescheduleChanges =
  (jobUuid: string, body: SubmitChangeRequestChangesParams) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = buildUrl(JOB_RESCHEDULING_CHANGE_REQUEST)
      .setPath("uuid", jobUuid)
      .done();

    const action = await asyncActionCreator({
      url,
      body,
      action: SUBMIT_RESCHEDULING_CHANGE_REQUEST,
      method: ASYNC_ACTION_TYPES.POST,
    });

    return dispatch(action);
  };

export const initAutomaticAgentReplacement =
  (jobUuid: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const url = buildUrl(AUTOMATIC_AGENT_REPLACEMENT)
      .setPath("uuid", jobUuid)
      .done();

    const action = await asyncActionCreator({
      url,
      action: INITIALIZE_AUTOMATIC_AGENT_REPLACEMENT,
    });

    return dispatch(action);
  };
